import { orderBy, QueryConstraint, where } from 'firebase/firestore';
import { useMemo } from 'react';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import { useNavigate } from 'react-router-dom';
import { useCollection } from 'core/hooks/useCollection';
import { IButton } from 'shared/components/buttons/button-interface';
import { TransportJobsApiService } from 'core/api';
import { ITransportJobDao } from 'core/api/types/transport-job.interface';
import { TransportJobStatusLabels } from 'core/constants/transport-job-status';
import SharedButton from 'shared/components/buttons/button';
import { useDialog } from 'core/providers/DialogProvider';
import TransportGroupsApiService from 'core/api/transport-groups/transport-groups-api.service';
import { ITransportGroup } from 'core/api/transport-groups/transport-groups-api-interface';
import BatchProgressDialog from './transport-groups/batch-progress-dialog';

const columns: ISharedTableColumn[] = [
  {
    // The number the requester was given. First, because a phone call starts
    // with it and this is the list somebody scans while taking that call.
    key: 'reference',
    label: 'Ref',
    templateId: 'reference',
    width: 6,
  },
  {
    key: 'required',
    label: 'Required',
    templateId: 'required',
    width: 5,
  },
  {
    key: 'requester',
    label: 'Requester',
    templateId: 'requester',
    width: 15,
  },
  {
    key: 'urgency',
    label: 'Urgent',
    templateId: 'urgency',
    width: 5,
  },
  {
    key: 'collection',
    label: 'Collection',
    templateId: 'collection',
    width: 15,
  },
  {
    key: 'destination',
    label: 'Destination',
    templateId: 'destination',
    width: 15,
  },
  {
    key: 'status',
    label: 'Status',
    width: 5,
    templateId: 'text',
  },
  {
    // Which batch a job is in is a fact about the job, so it gets a column of
    // its own rather than displacing the status of the work itself.
    key: 'batch',
    label: 'Batch',
    templateId: 'batch',
    width: 8,
  },
  {
    key: 'actions',
    label: '',
    templateId: 'actions',
    width: 5,
  },
];

interface IProps {
  /** The status the shell is narrowed to, or nothing for all of them. */
  status?: string;
}

const TransportJobsList = ({ status }: IProps) => {
  const dialog = useDialog();

  /**
   * Opens the batch a job belongs to.
   *
   * Fetched on the click rather than with every row: most jobs are in no batch
   * at all, and the reference the row shows is already on the job.
   */
  const openBatch = async (uid: string) => {
    const snapshot = await TransportGroupsApiService.get(uid);
    const group = snapshot.data() as ITransportGroup | undefined;
    if (group) dialog?.openDialog(<BatchProgressDialog group={group} />);
  };

  const navigate = useNavigate();
  const constraints = useMemo(() => {
    const acc: QueryConstraint[] = [orderBy('details.timestamp', 'desc')];
    if (status) {
      acc.push(where('status', '==', status));
    }
    return acc;
  }, [status]);

  const { data, loading, loadMore, hasMore, loadingMore } = useCollection<ITransportJobDao>({
    apiService: TransportJobsApiService,
    constraints,
  });

  const requiredAtTemplate = (details: ITransportJobDao['details']) => {
    const collectionAt = `${details.date} ${details.time}`;
    return <p>{collectionAt}</p>;
  };

  const requesterTemplate = (details: ITransportJobDao['details']) => {
    return (
      <div>
        <p>{details.name}</p>
        <p>{details.email}</p>
        <p>{details.phoneNumber}</p>
      </div>
    );
  };

  const collectionTemplate = (details: ITransportJobDao['details']) => {
    return (
      <div>
        <p>{details.site}</p>
        <p>{details.department}</p>
        <p>{details.collectionPoint}</p>
      </div>
    );
  };

  const urgencyTemplate = (details: ITransportJobDao['details']) => {
    return details.urgent ? <p>Yes</p> : <p>No</p>;
  };

  const destinationTemplate = (details: ITransportJobDao['details']) => {
    return (
      <div>
        {details.destination.formatted_address.map((line, index) => (
          <p key={index}>{line}</p>
        ))}
      </div>
    );
  };

  const actionCellTemplate = (details: ITransportJobDao) => {
    return (
      <div className='w-full flex justify-end'>
        <SharedButton
          onClick={() => navigate(`/transport/${details.uid}`)}
          type='button'
          appearance='subtle'
          spacing='compact'
          label='View'
        />
      </div>
    );
  };

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    {
      id: 'required',
      template: requiredAtTemplate,
    },
    {
      id: 'requester',
      template: requesterTemplate,
    },
    {
      id: 'collection',
      template: collectionTemplate,
    },
    {
      id: 'urgency',
      template: urgencyTemplate,
    },
    {
      id: 'destination',
      template: destinationTemplate,
    },
    {
      id: 'reference',
      template: (row: ITransportJobDao) =>
        row.requestReference ? (
          <p className='font-semibold'>{row.requestReference}</p>
        ) : (
          <span className='text-gray-400'>—</span>
        ),
    },
    {
      id: 'batch',
      template: (row: ITransportJobDao) =>
        row.groupUid ? (
          <button
            type='button'
            data-testid='job-batch'
            className='font-semibold text-blue-700 hover:underline'
            onClick={(event) => {
              // The row itself opens the job; this opens the batch around it.
              event.stopPropagation();
              openBatch(row.groupUid!);
            }}
          >
            {row.groupReference ?? 'Batch'}
          </button>
        ) : (
          <span className='text-gray-400'>—</span>
        ),
    },
    {
      id: 'actions',
      template: actionCellTemplate,
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'button',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  return (
    <div data-testid='transport-jobs' className='mt-4'>
      <SharedTable
        columns={columns}
        rows={(data ?? []).map((job) => ({
          key: job.uid,
          data: {
            ...job,
            ...job.details,
            status: TransportJobStatusLabels[job.status],
          },
        }))}
        isLoading={loading}
        customTemplates={customTemplates}
        loadMoreButton={hasMore ? loadMoreButton : undefined}
      />
    </div>
  );
};

export default TransportJobsList;
